import { v4 as uuidv4 } from 'uuid';

import {
  focusOnFirstValueInput,
  hasNestedObjects,
} from '@/components/PrettyJSONEditor/utils';
import {
  ExampleSchema,
  ExampleSchemaWithRunsAndOptionalFields,
} from '@/types/schema';

import { OPTIONAL_MODEL_SECRETS } from '../Playground.constants';
import { ExampleSchemaOrDraftWithEdited } from '../PlaygroundContext';
import { PlaygroundSecretsSource } from '../components/constants';

export const checkMissingSecrets = (
  secrets: Record<string, string>,
  selectedSecretsSource: PlaygroundSecretsSource,
  workspaceSecrets: Set<string>
) => {
  if (!secrets) return false;

  if (selectedSecretsSource === PlaygroundSecretsSource.WORKSPACE) {
    return Object.entries(secrets).some(
      ([key]) =>
        !workspaceSecrets.has(key) && !OPTIONAL_MODEL_SECRETS.includes(key)
    );
  }

  if (selectedSecretsSource === PlaygroundSecretsSource.BROWSER) {
    // Check if browser secrets are missing but workspace secrets are available
    return Object.entries(secrets).some(([key, value]) => {
      // If the secret is optional, it's not considered missing
      if (OPTIONAL_MODEL_SECRETS.includes(key)) return false;

      // If browser secret is missing but workspace has it, it's not considered missing
      if (!value && workspaceSecrets.has(key)) return false;

      // Otherwise, if browser secret is missing, it's considered missing
      return !value;
    });
  }

  return false;
};

export const hasAnyNestedExamples = (
  examples: ExampleSchema[] | ExampleSchemaWithRunsAndOptionalFields[]
): boolean => {
  return examples.some((example) => {
    const nestedInputs = hasNestedObjects(example.inputs);
    const nestedOutputs = hasNestedObjects(example.outputs);
    return nestedInputs || nestedOutputs;
  });
};

export function splitNewExamplesFromExistingEditedExamples(
  examples: ExampleSchemaOrDraftWithEdited[]
) {
  const { newExamples, editedExistingExamples } = examples.reduce(
    (acc, example) => {
      if (!example.dataset_id) {
        acc.newExamples.push(example);
      } else if (example.edited) {
        acc.editedExistingExamples.push(example);
      }
      return acc;
    },
    {
      newExamples: [] as ExampleSchemaOrDraftWithEdited[],
      editedExistingExamples: [] as ExampleSchemaOrDraftWithEdited[],
    }
  );
  return { newExamples, editedExistingExamples };
}

export const focusOnFirstExampleRowValue = (): void => {
  setTimeout(() => {
    const firstCellOfTable = document.querySelector(
      '.dataset-session-compare-table td'
    );
    if (firstCellOfTable) {
      focusOnFirstValueInput(firstCellOfTable);
    }
  }, 0);
};

export const createNewExample = ({
  inputVariables,
  outputVariables,
  inputValues,
  outputValues,
}: {
  inputVariables: string[];
  outputVariables: string[];
  inputValues?: Record<string, string>;
  outputValues?: Record<string, string>;
}) => {
  const inputs = inputValues
    ? inputValues
    : Object.fromEntries(inputVariables.map((key) => [key, '']));
  const outputs = outputValues
    ? outputValues
    : Object.fromEntries(outputVariables.map((key) => [key, '']));

  return {
    id: uuidv4(),
    name: '',
    created_at: new Date().toISOString(),
    modified_at: new Date().toISOString(),
    inputs,
    outputs,
    edited: Object.values({ ...inputs, ...outputs }).some(Boolean),
  };
};
